/**
 * advance-carrier-squadrons.ts — per-tick carrier squadron lifecycle (v3M17.2).
 *
 * Three responsibilities, run in this order each tick:
 *   1. Rebuild ticking — every `SQUADRON_REBUILD_TICKS_PER_FIGHTER` ticks,
 *      each carrier with `embarkedSquadronCount < CARRIER_SQUADRON_CAPACITY`
 *      accumulates +1 in `rebuildProgressFighters`. On hitting
 *      `SQUADRON_FIGHTER_COUNT`, spawn a docked squadron
 *      (`embarkedSquadronCount += 1`) and reset the accumulator. Halts
 *      while the carrier is mid-jump, EMP-disabled, or destroyed.
 *   2. Auto-recall — for each deployed squadron, check the parent
 *      carrier's `carrierDefenseBehaviour`. With 'return-at-low' the
 *      squadron recalls itself when its `liveFighterCount` drops to
 *      `SQUADRON_DEFENSE_RECALL_THRESHOLD` (= 2). 'no-surrender' never
 *      auto-recalls. Recall mechanics: despawn the squadron, increment
 *      `embarkedSquadronCount` (capped), credit surviving fighters to
 *      `rebuildProgressFighters` with rollover.
 *   3. Auto-launch — for each carrier with `carrierAttackBehaviour !==
 *      'none'`, `embarkedSquadronCount > 0`, and no own squadron
 *      currently deployed, check whether any team-visible enemy is in
 *      the configured range (visionRange / scanRange). If yes, spawn
 *      `embarkedSquadronCount` squadrons in a Vogel-spiral around the
 *      carrier and zero the count.
 *
 * Order is intentional: rebuild first so this tick's freshly-rebuilt
 * squadrons are eligible for launch; recall second so a low-count
 * deployed squadron returns home before launch evaluates "any deployed?";
 * launch third so a freshly-emptied carrier (recalled all squadrons) can
 * re-launch the same tick once an enemy enters range. The "no
 * re-launch while any deployed" gate naturally throttles thrash: the
 * carrier waits for its full wave to come home before sending another.
 *
 * Runs AFTER advance-combat so post-combat `liveFighterCount` decisions
 * are visible, and BEFORE advance-ai so the AI sees the freshly
 * launched/recalled state.
 *
 * v3M17.5 will hook the parent-carrier-destroyed sever path into
 * despawnShip and the carrier-jumps-with-deployed-squadrons auto-recall
 * pre-emption into the three jump appliers.
 */

import { despawnShip } from './advance-combat';
import { initWeaponSlots } from './scenario';
import {
  CARRIER_SQUADRON_CAPACITY,
  SQUADRON_CHASSIS_BY_RACE,
  SQUADRON_CHASSIS_NAMES,
  SQUADRON_DEFENSE_RECALL_THRESHOLD,
  SQUADRON_FIGHTER_COUNT,
  SQUADRON_REBUILD_TICKS_PER_FIGHTER,
  isCarrierTemplate,
} from './balance-carrier-squadrons';
import { effectiveMaxHull, effectiveMaxShields } from './research-effects';
import type {
  Empire,
  GameState,
  RaceId,
  ShipInstance,
  ShipInstanceId,
  ShipTemplate,
} from '../state/types';
import { allocEntityId } from '../state/types';

/**
 * Run one carrier-squadron tick. Mutates state. Idempotent on absent
 * carriers (no-op for empires / saves without any carrier-chassis ships).
 */
export function advanceCarrierSquadrons(state: GameState): void {
  // Snapshot the ship list before we start spawning — auto-launch can
  // add new squadron entries to state.ships and we don't want them
  // included in this tick's recall walk.
  const ships = [...state.ships.values()];

  // (1) Rebuild tick — gated on the global tick % cadence.
  if (state.tick % SQUADRON_REBUILD_TICKS_PER_FIGHTER === 0) {
    for (const ship of ships) {
      if (ship.embarkedSquadronCount === undefined) continue;
      tickRebuild(state, ship);
    }
  }

  // (2) Auto-recall — walk deployed squadrons, dispatch by Defense.
  for (const ship of ships) {
    if (ship.parentCarrierShipId === undefined) continue;
    if (ship.currentHull <= 0) continue;
    autoRecallIfTriggered(state, ship);
  }

  // (3) Auto-launch — walk carriers, dispatch by Attack.
  for (const ship of ships) {
    if (ship.embarkedSquadronCount === undefined) continue;
    if (ship.currentHull <= 0) continue;
    autoLaunchIfTriggered(state, ship);
  }
}

// ---------------------------------------------------------------------------
// Rebuild
// ---------------------------------------------------------------------------

/**
 * Tick the carrier's `rebuildProgressFighters` by 1. On hitting
 * `SQUADRON_FIGHTER_COUNT`, spawn a docked squadron (increment
 * `embarkedSquadronCount`, capped at `CARRIER_SQUADRON_CAPACITY`) and
 * reset the accumulator. Skips when at-cap, mid-jump, EMP-disabled,
 * or destroyed (the destroyed check is handled in the caller).
 */
function tickRebuild(state: GameState, carrier: ShipInstance): void {
  const docked = carrier.embarkedSquadronCount ?? 0;
  if (docked >= CARRIER_SQUADRON_CAPACITY) return;
  if (carrier.systemId === null) return; // mid-jump
  if (carrier.disabledTicks > 0) return; // EMP

  let progress = (carrier.rebuildProgressFighters ?? 0) + 1;
  if (progress >= SQUADRON_FIGHTER_COUNT) {
    if (docked < CARRIER_SQUADRON_CAPACITY) {
      carrier.embarkedSquadronCount = docked + 1;
      progress -= SQUADRON_FIGHTER_COUNT;
    } else {
      // Cap-discard branch — should be unreachable due to the guard
      // above but keeps the rollover loop closed defensively.
      progress = SQUADRON_FIGHTER_COUNT - 1;
    }
  }
  carrier.rebuildProgressFighters = progress;
  void state; // keep signature consistent with other helpers in this file
}

// ---------------------------------------------------------------------------
// Auto-recall
// ---------------------------------------------------------------------------

/**
 * If the squadron's parent carrier has Defense Behaviour `'return-at-low'`
 * and the squadron's `liveFighterCount` has dropped to
 * `SQUADRON_DEFENSE_RECALL_THRESHOLD` or below, recall it. `'no-surrender'`
 * never auto-recalls — squadron rides until destroyed.
 */
function autoRecallIfTriggered(state: GameState, squadron: ShipInstance): void {
  const carrier = squadron.parentCarrierShipId !== undefined
    ? state.ships.get(squadron.parentCarrierShipId)
    : undefined;
  if (!carrier || carrier.currentHull <= 0) return; // sever path handles
  const defense = carrier.carrierDefenseBehaviour;
  if (defense !== 'return-at-low') return;
  const live = squadron.liveFighterCount ?? 0;
  if (live > SQUADRON_DEFENSE_RECALL_THRESHOLD) return;
  recallSquadron(state, squadron, carrier);
}

/**
 * Recall mechanics — shared between the auto-recall pass and the
 * `recallCarrierSquadrons` command. Despawn the squadron, increment
 * the carrier's `embarkedSquadronCount` (capped at
 * `CARRIER_SQUADRON_CAPACITY`), and credit `liveFighterCount` to
 * `rebuildProgressFighters` with rollover (each rollover spawning
 * another docked squadron up to cap; excess is discarded).
 */
export function recallSquadron(
  state: GameState,
  squadron: ShipInstance,
  carrier: ShipInstance,
): void {
  const fighters = squadron.liveFighterCount ?? 0;
  despawnShip(state, squadron.id);

  const dockedAfterReturn = Math.min(
    CARRIER_SQUADRON_CAPACITY,
    (carrier.embarkedSquadronCount ?? 0) + 1,
  );
  carrier.embarkedSquadronCount = dockedAfterReturn;

  let progress = (carrier.rebuildProgressFighters ?? 0) + fighters;
  while (progress >= SQUADRON_FIGHTER_COUNT) {
    // Explicit annotation to break the inference cycle: `current` is
    // initialised from `carrier.embarkedSquadronCount` and the same
    // field is re-assigned inside the loop, so TS without an annotation
    // widens the type to `any`.
    const current: number = carrier.embarkedSquadronCount ?? 0;
    if (current < CARRIER_SQUADRON_CAPACITY) {
      carrier.embarkedSquadronCount = current + 1;
      progress -= SQUADRON_FIGHTER_COUNT;
    } else {
      // At cap — discard the rest of the rebuild credit.
      progress = SQUADRON_FIGHTER_COUNT - 1;
      break;
    }
  }
  carrier.rebuildProgressFighters = progress;
}

// ---------------------------------------------------------------------------
// Auto-launch
// ---------------------------------------------------------------------------

/**
 * Fire a launch on the carrier when Attack Behaviour is satisfied and
 * the carrier holds docked squadrons with no own deployed. Spawns
 * `embarkedSquadronCount` squadrons in a Vogel-spiral around the
 * carrier and zeros the count.
 */
function autoLaunchIfTriggered(state: GameState, carrier: ShipInstance): void {
  const attack = carrier.carrierAttackBehaviour;
  if (attack === undefined || attack === 'none') return;
  if (carrier.systemId === null) return;
  if (carrier.disabledTicks > 0) return;
  if (carrier.cloaked) return;
  const docked = carrier.embarkedSquadronCount ?? 0;
  if (docked === 0) return;
  if (anyOwnSquadronDeployed(state, carrier)) return;

  const empire = state.empires.get(carrier.ownerEmpireId);
  if (!empire) return;
  const tmpl = state.shipTemplates.get(`${empire.raceId}:${carrier.chassis}`);
  if (!tmpl) return;
  if (!isCarrierTemplate(tmpl)) return;

  if (!anyEnemyInLaunchRange(state, carrier, empire, tmpl, attack)) return;

  launchAllDocked(state, carrier, empire);
}

/**
 * True iff at least one squadron in `state.ships` has
 * `parentCarrierShipId === carrier.id`. Prevents re-launch while any
 * of the carrier's own squadrons are still deployed (per design lock —
 * launch cycle ends only when ALL squadrons return).
 */
function anyOwnSquadronDeployed(state: GameState, carrier: ShipInstance): boolean {
  for (const ship of state.ships.values()) {
    if (ship.parentCarrierShipId === carrier.id) return true;
  }
  return false;
}

/**
 * Walk team-vision for the carrier's empire. For 'visual-range' check
 * each enemy in the carrier's system seen via the 'vision' channel
 * within the carrier's template `visionRange`. For 'scan-range', check
 * 'scan' channel within `scanRange`. Returns true on first match.
 */
function anyEnemyInLaunchRange(
  state: GameState,
  carrier: ShipInstance,
  empire: Empire,
  tmpl: ShipTemplate,
  attack: 'visual-range' | 'scan-range',
): boolean {
  const range = attack === 'visual-range' ? tmpl.visionRange : tmpl.scanRange;
  const channel = attack === 'visual-range' ? 'vision' : 'scan';
  const teamMap = state.teamVision.get(empire.id);
  if (!teamMap) return false;
  const r2 = range * range;
  for (const rec of teamMap.values()) {
    if (rec.seenVia !== channel) continue;
    if (rec.lastSeenSystemId !== carrier.systemId) continue;
    const enemy = state.ships.get(rec.shipId);
    if (!enemy) continue;
    if (enemy.currentHull <= 0) continue;
    if (enemy.systemId !== carrier.systemId) continue;
    const dx = enemy.sx - carrier.sx;
    const dy = enemy.sy - carrier.sy;
    if (dx * dx + dy * dy <= r2) return true;
  }
  return false;
}

/**
 * Spawn `embarkedSquadronCount` squadrons in a Vogel-spiral around the
 * carrier, then zero the count. Shared between auto-launch and the
 * `launchCarrierSquadrons` command. Each squadron is a fresh
 * ShipInstance with `liveFighterCount: 5`, `parentCarrierShipId: carrier.id`,
 * and weaponSlots derived from the squadron template loadout.
 */
export function launchAllDocked(
  state: GameState,
  carrier: ShipInstance,
  empire: Empire,
): void {
  const docked = carrier.embarkedSquadronCount ?? 0;
  if (docked === 0) return;
  if (carrier.systemId === null) return;

  const squadronChassis = SQUADRON_CHASSIS_BY_RACE[empire.raceId as RaceId];
  const squadronTmpl = state.shipTemplates.get(
    `${empire.raceId}:${squadronChassis}`,
  );
  if (!squadronTmpl) return;

  const spacing = state.balanceData.movement.formationSpacing;
  for (let i = 0; i < docked; i++) {
    const offset = vogelOffset(i, spacing);
    const sx = clamp01(carrier.sx + offset.dx);
    const sy = clamp01(carrier.sy + offset.dy);
    spawnSquadron(state, carrier, empire, squadronTmpl, sx, sy);
  }
  carrier.embarkedSquadronCount = 0;
}

/**
 * Vogel-spiral offset for the i-th squadron at standard formation
 * spacing. Mirrors the formation pattern used by `applyMoveShips` and
 * v3M18 warp-link travel — keeps squadrons from spawning on top of
 * each other.
 */
function vogelOffset(i: number, spacing: number): { dx: number; dy: number } {
  const goldenAngle = Math.PI * (3 - Math.sqrt(5));
  const r = spacing * Math.sqrt(i + 1);
  const theta = goldenAngle * i;
  return { dx: Math.cos(theta) * r, dy: Math.sin(theta) * r };
}

function clamp01(n: number): number {
  if (n < 0) return 0;
  if (n > 1) return 1;
  return n;
}

/**
 * Spawn a single squadron at (sx, sy) parented to the given carrier.
 * Allocates a new ShipInstanceId, stamps `liveFighterCount: 5` and
 * `parentCarrierShipId`, and initialises weapon slots from the squadron
 * template. Mirrors the spawn pattern in advance-shipyard's
 * `spawnBuiltShip` but specific to squadrons.
 */
function spawnSquadron(
  state: GameState,
  carrier: ShipInstance,
  empire: Empire,
  squadronTmpl: ShipTemplate,
  sx: number,
  sy: number,
): ShipInstanceId {
  const ship: ShipInstance = {
    id: allocEntityId(state),
    chassis: squadronTmpl.chassis,
    ownerEmpireId: empire.id,
    // Hull/shields drawn from the 5x aggregate template — full strength
    // at spawn. v3M17.3 will wire the variable-count HP pool damage
    // chain so currentHull tracks `liveFighterCount × parent.hull`.
    currentHull: effectiveMaxHull(state, empire, squadronTmpl),
    currentShields: effectiveMaxShields(state, empire, squadronTmpl),
    currentEnergy: squadronTmpl.energyCapacity ?? 0,
    disabledTicks: 0,
    cloaked: false,
    systemId: carrier.systemId,
    sx,
    sy,
    moveOrder: null,
    jumpOriginSystemId: null,
    jumpTargetSystemId: null,
    jumpArrivalAngle: null,
    jumpTargetGateId: null,
    pendingWarpGateLinkId: null,
    jumpTicksRemaining: 0,
    chargeUpTicksRemaining: 0,
    jumpFuelCostPaid: 0,
    jumpDurationTotal: 0,
    lastFiredTick: null,
    lastFiredTargetShipId: null,
    pendingJumpSystemId: null,
    pendingJumpArrivalAngle: null,
    pendingColoniseTargetId: null,
    constructionOrder: null,
    bombardOrder: null,
    weaponSlots: initWeaponSlots(
      squadronTmpl.weaponLoadout,
      state.weapons,
      empire.researchedTechs,
    ),
    liveFighterCount: SQUADRON_FIGHTER_COUNT,
    parentCarrierShipId: carrier.id,
    // v3M22.1 — ship-commands defaults; squadrons are tactical-AI-driven
    // once deployed (auto-engage nearest enemy), never accept player
    // patrol/explore/guard commands.
    commandKind: 'idle',
    waypoints: [],
    guardTargetKind: null,
    guardTargetId: null,
  };
  state.ships.set(ship.id, ship);
  empire.shipIds.push(ship.id);
  return ship.id;
}

/**
 * Recall every squadron whose `parentCarrierShipId === carrier.id`.
 * Shared between the manual `recallCarrierSquadrons` command and the
 * carrier-jumps auto-recall pre-emption (v3M17.5).
 */
export function recallAllOwnSquadrons(
  state: GameState,
  carrier: ShipInstance,
): void {
  const targets: ShipInstance[] = [];
  for (const ship of state.ships.values()) {
    if (ship.parentCarrierShipId === carrier.id) targets.push(ship);
  }
  for (const squadron of targets) recallSquadron(state, squadron, carrier);
}

/** Used by tests and v3M17.5 sever-path. Squadrons of `carrier`. */
export function findOwnSquadrons(
  state: GameState,
  carrier: ShipInstance,
): ShipInstance[] {
  const out: ShipInstance[] = [];
  for (const ship of state.ships.values()) {
    if (ship.parentCarrierShipId === carrier.id) out.push(ship);
  }
  return out;
}

/** Squadron-chassis predicate for downstream filters. */
export function isSquadron(ship: ShipInstance): boolean {
  return SQUADRON_CHASSIS_NAMES.has(ship.chassis);
}
